/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

document.addEventListener("DOMContentLoaded", function() {
  if(document.body.classList.contains('template-product')){
    // Show the first tab and hide the rest
    document.querySelector('#tabs-nav li:first-child').classList.add('active');
    document.querySelectorAll('.tab-content-main').forEach(function(tabContent, index) {
      if (index !== 0) {
        tabContent.style.display = 'none';
      }
    });
  
    // Click function
    document.querySelectorAll('#tabs-nav li').forEach(function(tab) {
      tab.addEventListener('click', function(event) {
        event.preventDefault();
        document.querySelectorAll('#tabs-nav li').forEach(function(tab) {
          tab.classList.remove('active');
        });
        tab.classList.add('active');
        document.querySelectorAll('.tab-content-main').forEach(function(tabContent) {
          tabContent.style.display = 'none';
        });
  
        var activeTab = tab.querySelector('a').getAttribute('href');
        document.querySelector(activeTab).style.display = 'block';
      });
    });
  }
  
  // for moving tab to bottom on mobile PDP
  function moveProductTabIfNeeded() {
    if (window.innerWidth < 768) {
      var productTabWr = document.getElementById("product_tab_wr");
      var mobileTab = document.getElementById("mobile_tab");
  
      if (productTabWr && mobileTab) {
        mobileTab.appendChild(productTabWr);
      }
    }
  }
  moveProductTabIfNeeded();
  window.addEventListener('resize', moveProductTabIfNeeded);

});
$(document).ready(function() {
  $(document).on('click', '.navigation_first_level .navigation_first_level_link', function(e) {
    e.preventDefault();
    var $tier2Container = $(this).parents('.navigation_first_level').find('.navigation__tier-2-container');
    // $('.navigation__tier-2-container').not($tier2Container).slideUp();
    if ($tier2Container.is(':visible')) {
      $tier2Container.stop(true, true).hide();
      $(this).removeClass('rotate_arrow');
    } else {
      $tier2Container.stop(true, true).show();
      $(this).addClass('rotate_arrow');
    }
  });

  function search_result_position(){
    var cart_width = $('.cart_with_progress').innerWidth() + 15;
    $('.main-search__results').css('right', cart_width);
  }
  
  search_result_position();

  // $(document).on('click','.main-search__form', function(e){
  //   e.preventDefault();
  // });

  // Price reduce functionality on product page and collection page
  function formatPrice(price) {
    // Ensure the price is formatted to two decimal places
    let priceString = price.toFixed(2);
  
    // Split the price into integer and decimal parts
    let parts = priceString.split('.');
    let integerPart = parts[0];
    let decimalPart = parts[1];
  
    let formattedInteger;
    if (integerPart.length === 5) {
      // Insert dot after the second digit for 5-digit numbers
      formattedInteger = integerPart.slice(0, 2) + '.' + integerPart.slice(2);
    } else if (integerPart.length === 4) {
      // Insert dot after the first digit for 4-digit numbers
      formattedInteger = integerPart.slice(0, 1) + '.' + integerPart.slice(1);
    } else {
      // Leave other numbers unchanged
      formattedInteger = integerPart;
    }
  
    // Combine the parts to create the final formatted string
    let formattedPrice = formattedInteger + ',' + decimalPart + ' Kr';
  
    return formattedPrice;
  }

  // Show discounted price according to metafield on product page
  if ($('body').hasClass('template-product')) {
    function updatePrice() {
      var its_quantity = $('#quantity').val();
      var its_price = parseFloat($('.custom_price').data('price'));
      var its_discount_percent = parseFloat($('.custom_price').data('discount'));
      if(!isNaN(its_discount_percent)){
        if (!isNaN(its_discount_percent) && its_quantity >= 6) {
          var discounted_price = its_price * (1 - (its_discount_percent / 100)); 
          $('.price__current').text(formatPrice(discounted_price));
        } else {
          $('.price__current').text(formatPrice(its_price));
        }
      }
    }
    function updatePriceOnload() {
      var its_quantity = $('#quantity').val();
      var its_price = parseFloat($('.custom_price').data('price'));
      var its_discount_percent = parseFloat($('.custom_price').data('discount'));
      if (!isNaN(its_discount_percent) && its_quantity >= 6) {
        var discounted_price = its_price * (1 - (its_discount_percent / 100));
        $('.price__current').text(formatPrice(discounted_price));
      } 
    }

    // Store the original price as a data attribute
    var original_price_text = $('.price__current').text();
    var original_price = parseFloat(original_price_text.replace(',', '.').replace(' Kr', ''));
    $('.price__current').data('original-price', original_price);

    var its_discount = parseFloat($('.custom_price').data('discount'));
    if (!isNaN(its_discount)) {
      // Initial check and update on page load
      updatePriceOnload();
    
      // Listen for quantity changes
      $('#quantity').on('input change', function() {
        updatePrice();
      });
    }
  }

// Show discounted price according to metafield on product cards
  function updateCardPrice(card) {
    var its_quantity = $(card).find('#quantity').val();
    var its_price = parseFloat($(card).find('.custom_price').data('price'));
    var its_discount_percent = parseFloat($(card).find('.custom_price').data('discount'));
    
    if(!isNaN(its_discount_percent)){
      if (!isNaN(its_discount_percent) && its_quantity >= 6 && its_discount_percent != '') {
        var discounted_price = its_price * (1 - (its_discount_percent / 100));
        $(card).find('.price__current').text(formatPrice(discounted_price));
      } else {
        $(card).find('.price__current').text(formatPrice(its_price));
      }
    }
    
  }
  function updateCardPriceOnLoad(card) {
    var its_quantity = $(card).find('#quantity').val();
    var its_price = parseFloat($(card).find('.custom_price').data('price'));
    var its_discount_percent = parseFloat($(card).find('.custom_price').data('discount'));
    
    
    if (!isNaN(its_discount_percent) && its_quantity >= 6 && its_discount_percent != '') {
      var discounted_price = its_price * (1 - (its_discount_percent / 100));
      $(card).find('.price__current').text(formatPrice(discounted_price));
    } 
  }

  function initializeCard(card) {
    // Store the original price as a data attribute if not already stored
    if (!$(card).find('.price__current').data('original-price')) {
      var original_price_text = $(card).find('.price__current').text();
      var original_price = parseFloat(original_price_text.replace(',', '.').replace(' Kr', ''));
      $(card).find('.price__current').data('original-price', original_price);
    }
    // Initial check and update on page load
    updateCardPriceOnLoad(card);
    // Listen for quantity changes within the card
    $(card).find('#quantity').on('input change', function() {
      updateCardPrice(card);
    });
  }
  // Initialize all cards
  $('.product-block').each(function() {
      initializeCard(this);
  });  

  function reorderDivs() {
    if ($(window).width() <= 767) {
      $('.ingredients_main').after($('.features_main'));
    } else {
      $('.features_main').after($('.ingredients_main'));
    }
  }

  // Initial check
  reorderDivs();

  // Re-check on window resize
  $(window).resize(function() {
    reorderDivs();
    search_result_position();
  });

  // variant click popup button for contact js
  if($(document).find('.has_specialmal').length){
    function variant_select(){
      $('.option-selector__btns input').each(function(){
        var checked_input = $(this).is(':checked');
        var input_value = $(this).val();
        if (checked_input && input_value == 'Specialmål (Få et tilbud)') {
          $('.buy-buttons-row').hide();
          var button_main = $('.buy-buttons-row');
          $('.custom_contact_button').css('display', 'flex');
        }else{
          $('.buy-buttons-row').show();
          $('.custom_contact_button').hide();
        }
      });
    }
    variant_select();
    $(document).on('change', '.option-selector__btns input', function(){
      variant_select();
    })
  }
});

document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('.show-search-link_cstm').forEach(function(element) {
  element.addEventListener('click', function(e) {
    e.preventDefault();
    
    var mobileNavToggle = document.querySelector('.mobile-nav-toggle');
    if (mobileNavToggle) {
      mobileNavToggle.click();
    }
    
    var searchInputs = document.querySelectorAll('.main-search__form .main-search__input');
    var visibleSearchInput = null;

    searchInputs.forEach(function(input) {
      if (input.offsetParent !== null) { // Check if the element is visible
        visibleSearchInput = input;
      }
    });

    if (visibleSearchInput) {
      setTimeout(function() {
        visibleSearchInput.focus();
      }, 100); // Delay to ensure the element is ready
    }
  });
});

});


